import type { PixelBuffer } from "../pixel/PixelBuffer";
import type { LayerStyles } from "./LayerStyle";
import type { Document } from "./Document";
import type { PathModel } from "./PathModel";

export type LayerId = string;

// Full Photoshop blend-mode set (27 modes). Kebab-case keys.
// Names line up 1:1 with PARITY.md and the Photoshop UI labels.
export type BlendMode =
  | "normal"
  | "pass"
  | "dissolve"
  // Darken group
  | "darken"
  | "multiply"
  | "color-burn"
  | "linear-burn"
  | "darker-color"
  // Lighten group
  | "lighten"
  | "screen"
  | "color-dodge"
  | "linear-dodge"
  | "lighter-color"
  // Contrast group
  | "overlay"
  | "soft-light"
  | "hard-light"
  | "vivid-light"
  | "linear-light"
  | "pin-light"
  | "hard-mix"
  // Inversion / cancellation group
  | "difference"
  | "exclusion"
  | "subtract"
  | "divide"
  // Component group
  | "hue"
  | "saturation"
  | "color"
  | "luminosity";

export type LayerColorLabel = "none" | "red" | "orange" | "yellow" | "green" | "blue" | "violet" | "gray";

export interface LayerBase {
  readonly id: LayerId;
  readonly name: string;
  readonly visible: boolean;
  readonly opacity: number; // 0..1
  readonly fillOpacity: number; // 0..1 (Photoshop "Fill" slider)
  readonly blendMode: BlendMode;
  x: number; // document-space position of the layer's top-left
  y: number;
  /** Layer locks (Photoshop lock row). */
  readonly lockTransparency: boolean;
  readonly lockImage: boolean;
  readonly lockPosition: boolean;
  readonly lockAll: boolean;
  /**
   * When true, this layer is clipped to the layer below. Its content is only
   * visible where the base layer (the nearest non-clipped layer below it) has
   * non-transparent pixels. Photoshop equivalent: Alt+click between layers.
   */
  readonly clippedToBelow: boolean;
  /**
   * Color label for organizing layers in the Layers panel (Photoshop: right-click → layer color).
   */
  readonly colorLabel?: LayerColorLabel;
  /**
   * Photoshop-style layer effects (Drop Shadow, Inner Glow, Bevel & Emboss,
   * …).  Optional; absent means no styles are configured on this layer.
   * Defined in `./LayerStyle.ts` so this file stays focused on the core tree.
   */
  readonly styles?: LayerStyles;
  /**
    * Blend If — luminance/channel range that controls which underlying pixels
    * this layer blends with. Per-channel (Gray, R, G, B) with This Layer and
    * Underlying Layer slider pairs. Each slider has a [min, max] range; pixels
    * outside the range are excluded from blending. Split sliders (Alt+drag)
    * create a soft transition zone.
    */
  readonly blendIf?: BlendIfConfig;
  /**
   * Linked layers — IDs of layers linked to this one for joint transforms.
   * Photoshop equivalent: Link layers button at bottom of Layers panel.
   */
  readonly linkedLayerIds?: readonly LayerId[];
  /**
   * Optional vector mask — a path that clips the layer content.  Present on
   * any layer kind (raster, type, group, smart-object, shape …).  The shape
   * layer's own `vectorMask` field is the canonical per-kind declaration;
   * this base field allows the renderer and other generic paths to access it
   * without a kind-specific cast (fixes #194).
   */
  readonly vectorMask?: import("./PathModel").PathModel | null;
}

/** Blend If configuration per channel. */
export interface BlendIfConfig {
  /** Per-channel blend-if ranges. Key: "gray" | "red" | "green" | "blue". */
  readonly channels?: {
    [key: string]: {
      /** This Layer: [blackPoint, whitePoint] — layer pixels outside this range become transparent. */
      thisLayer: [number, number]; // 0..255
      /** Underlying Layer: [blackPoint, whitePoint] — underlying pixels outside this range show through. */
      underlyingLayer: [number, number]; // 0..255
    };
  };
}

export interface RasterLayer extends LayerBase {
  readonly kind: "raster";
  pixels: PixelBuffer;
  /**
   * Optional raster mask. When present and `enabled`, controls per-pixel
   * visibility during compositing (0 = hidden, 255 = visible). See
   * `LayerMask` below.
   */
  readonly mask?: LayerMask;
}

/**
 * Per-layer raster mask (Photoshop "Layer Mask"). Adds an extra alpha channel
 * applied on top of the layer's own alpha at render time.
 *
 * Storage choice — RGBA8 reuse vs single-channel buffer:
 *
 *   The mask values are stored in the alpha channel of a regular `PixelBuffer`
 *   (R = G = B = 0, A = 0..255). This is 4x the memory of a packed
 *   single-channel buffer, but it lets us reuse the entire existing pixel
 *   infrastructure unchanged: version tracking, the renderer's bitmap cache,
 *   `PaintStrokeCommand`-style patch capture, brush stamping, blur filters,
 *   PSD I/O scratch, and so on. Adding a new `MaskBuffer` kind would force
 *   every one of those code paths to fork. The 3x waste is acceptable for M3;
 *   we can move to single-channel storage in M6 (color depth refactor)
 *   without touching anything above the renderer.
 */
export interface LayerMask {
  readonly pixels: PixelBuffer; // 1-byte-per-pixel mask stored in the alpha channel
  readonly enabled: boolean;    // user can disable the mask without deleting it
  readonly linked: boolean;     // moves with the layer (true) or independently (false)
  readonly x: number;           // mask position relative to the layer (usually 0)
  readonly y: number;
  readonly density: number;     // 0..1 (Photoshop "Density" slider)
  readonly feather: number;     // px (Photoshop "Feather" slider)
}

export interface GroupLayer extends LayerBase {
  readonly kind: "group";
  readonly children: readonly Layer[];
}

export type AdjustmentKind =
  | "brightness-contrast"
  | "levels"
  | "curves"
  | "hue-saturation"
  | "color-balance"
  | "black-white"
  | "photo-filter"
  | "gradient-map"
  | "invert"
  | "posterize"
  | "threshold"
  | "solid-color"
  | "exposure"
  | "vibrance"
  | "channel-mixer"
  | "color-lookup"
  | "selective-color";

export interface AdjustmentLayer extends LayerBase {
  readonly kind: "adjustment";
  readonly adjustment: AdjustmentKind;
  readonly params: Record<string, number | string | boolean>;
  readonly mask?: LayerMask;
}

/**
 * Smart Object layer — an embedded document container that enables
 * non-destructive transforms and smart filters. Photoshop equivalent:
 * Layer → Smart Objects → Convert to Smart Object.
 *
 * The embedded document is a full Document tree (layers, selection, etc.).
 * Rendering composites the embedded doc and draws it at the layer's
 * position/scale. Editing opens the embedded doc in a new tab.
 */
export interface SmartObjectLayer extends LayerBase {
  readonly kind: "smart-object";
  /** The embedded document that this Smart Object contains. */
  readonly embedded: Document;
  /** Cached composite of the embedded document (RGBA8). Regenerated when embedded changes. */
  readonly composite?: PixelBuffer;
  /**
   * Transform applied to the Smart Object's rendering (non-destructive).
   * These are separate from the layer's x/y position — they affect how the
   * embedded content is drawn (scale, rotation).
   */
  readonly transform: SmartObjectTransform;
  /**
   * Non-destructive filter stack. Filters are applied in order to the
   * Smart Object's composite before it is drawn to the document. Each filter
   * can be independently enabled/disabled, have its opacity adjusted, and
   * use a specific blend mode (Photoshop Smart Filters).
   */
  readonly smartFilters: readonly SmartFilterEntry[];
}

/**
 * A single smart filter entry in a Smart Object's filter stack.
 * Photoshop equivalent: the expandable filter list under a Smart Object
 * in the Layers panel.
 */
export interface SmartFilterEntry {
  readonly id: string; // unique within the Smart Object, e.g. "sf-1"
  /** Filter identifier matching Filter.id, e.g. "gaussian-blur". */
  readonly filterId: string;
  /** Filter parameters (matching the filter's TParams type). */
  readonly params: Record<string, number | string | boolean>;
  /** Whether this filter is currently active (eye icon in PS). */
  readonly enabled: boolean;
  /** Opacity of this filter's effect (0..1). Photoshop: double-click filter → opacity. */
  readonly opacity: number;
  /** Blend mode for this filter's result (0..1 maps to Normal..Luminosity). */
  readonly blendMode: BlendMode;
}

export type ShapeFillRule = "nonzero" | "evenodd";

export interface ShapeLayer extends LayerBase {
  readonly kind: "shape";
  readonly path: PathModel;
  readonly fillColor: { r: number; g: number; b: number; a: number } | null;
  readonly strokeColor: { r: number; g: number; b: number; a: number } | null;
  readonly strokeWidth: number;
  readonly fillRule: ShapeFillRule;
  readonly lineCap: "butt" | "round" | "square";
  readonly lineJoin: "miter" | "round" | "bevel";
  readonly miterLimit: number;
  /** Optional vector mask — a path that clips the layer content. */
  readonly vectorMask?: PathModel | null;
}

/**
 * Solid Fill Layer — fills entire layer area with a solid color.
 * Photoshop equivalent: Layer > New Fill Layer > Solid Color.
 */
export interface SolidFillLayer extends LayerBase {
  readonly kind: "solid-fill";
  readonly color: { r: number; g: number; b: number; a: number };
  readonly mask?: LayerMask;
}

/**
 * Gradient Fill Layer — fills with a gradient.
 * Photoshop equivalent: Layer > New Fill Layer > Gradient.
 */
export interface GradientFillLayer extends LayerBase {
  readonly kind: "gradient-fill";
  readonly gradientType: "linear" | "radial" | "angle" | "reflected" | "diamond";
  readonly angle: number; // degrees
  readonly scale: number; // 0..100
  readonly reverse: boolean;
  readonly dither: boolean;
  readonly stops: GradientStop[];
  readonly mask?: LayerMask;
}

export interface GradientStop {
  position: number; // 0..1
  color: { r: number; g: number; b: number; a: number };
  midpoint?: number; // 0..1, default 0.5
}

/**
 * Pattern Fill Layer — fills with a repeating pattern.
 * Photoshop equivalent: Layer > New Fill Layer > Pattern.
 */
export interface PatternFillLayer extends LayerBase {
  readonly kind: "pattern-fill";
  readonly pattern: { pixels: Uint8ClampedArray; width: number; height: number } | null;
  readonly patternName: string;
  readonly scale: number; // 0..100
  readonly linkWithLayer: boolean;
  readonly mask?: LayerMask;
}

/**
 * Background Layer — locked position, no transparency, always at bottom.
 * Photoshop equivalent: the default "Background" layer in new documents.
 */
export interface BackgroundLayer extends LayerBase {
  readonly kind: "background";
  readonly pixels: PixelBuffer;
}

/**
 * Artboard Layer — defines a rectangular working area with its own background.
 * Photoshop equivalent: Artboard tool.
 */
export interface ArtboardLayer extends LayerBase {
  readonly kind: "artboard";
  readonly artboardX: number;
  readonly artboardY: number;
  readonly artboardWidth: number;
  readonly artboardHeight: number;
  readonly backgroundColor: { r: number; g: number; b: number; a: number };
  readonly children: readonly Layer[];
}

/**
 * Frame Layer — placeholder container for image/content placement.
 * Photoshop equivalent: Frame Tool (K).
 */
export interface FrameLayer extends LayerBase {
  readonly kind: "frame";
  readonly shape: "rect" | "ellipse" | "rounded-rect";
  readonly cornerRadius: number;
  /** Content placed inside the frame (linked smart object or raster). */
  readonly content?: Layer | null;
  readonly frameWidth: number;
  readonly frameHeight: number;
}

/**
 * Linked Smart Object layer — references an external file instead of embedding.
 * Photoshop equivalent: File → Place Linked, or Layer → Smart Objects → Convert to Linked Smart Object.
 * When the external file changes, the Smart Object updates automatically.
 */
export interface LinkedSmartObjectLayer extends LayerBase {
  readonly kind: "linked-smart-object";
  /** Absolute or relative path to the external file. */
  readonly filePath: string;
  /** Last known modification time of the external file (for change detection). */
  readonly lastModified?: number;
  /** Cached composite of the linked file (RGBA8). Regenerated when file changes. */
  readonly composite?: PixelBuffer;
  /** Transform applied to the linked Smart Object's rendering. */
  readonly transform: SmartObjectTransform;
  /** Non-destructive filter stack. */
  readonly smartFilters: readonly SmartFilterEntry[];
  /** Whether the linked file is currently available. */
  readonly isMissing: boolean;
}

export type FillLayer = SolidFillLayer | GradientFillLayer | PatternFillLayer;

/**
 * Video Frame Layer — a single frame from a video timeline.
 * Photoshop equivalent: a layer created by placing a video file (File → Place)
 * or opening a video document. The timeline panel controls playback.
 *
 * Rendering is stubbed — the composite PixelBuffer is the rendered frame at
 * the current time position. Full video decoding is deferred to M6.
 */
export interface VideoFrameLayer extends LayerBase {
  readonly kind: "video-frame";
  /** Absolute or relative path to the source video file. */
  readonly filePath: string;
  /** Frame number (0-based) currently displayed. */
  readonly currentFrame: number;
  /** Total number of frames in the video (0 if unknown). */
  readonly totalFrames: number;
  /** Frames per second of the source video. */
  readonly fps: number;
  /** Width of a single video frame in pixels. */
  readonly videoWidth: number;
  /** Height of a single video frame in pixels. */
  readonly videoHeight: number;
  /**
   * Cached composite of the current frame (RGBA8).
   * Placeholder rendering shows a grey "Coming soon" tile until
   * actual video decoding is wired in M6.
   */
  readonly composite?: PixelBuffer;
  /** Whether the source file is currently available on disk. */
  readonly isMissing: boolean;
  /** Optional raster mask. */
  readonly mask?: LayerMask;
}

/**
 * 3D Layer — a layer containing a 3D model.
 * Photoshop equivalent: 3D > New 3D Layer from File, or the 3D menu.
 *
 * Rendering is stubbed — the composite PixelBuffer is the rendered 3D scene.
 * Full 3D compositing (lighting, materials, camera) is deferred to M7.
 */
export interface ThreeDLayer extends LayerBase {
  readonly kind: "3d";
  /** Absolute or relative path to the 3D model file (.obj, .3ds, .dae, …). */
  readonly filePath: string;
  /** Name of the scene / mesh for display in the 3D panel. */
  readonly sceneName: string;
  /**
   * Cached composite of the 3D scene render (RGBA8).
   * Placeholder rendering shows a grey "Coming soon" tile until
   * actual 3D rendering is wired.
   */
  readonly composite?: PixelBuffer;
  /** Camera position in the 3D scene. */
  readonly camera: {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly rotationX: number; // degrees
    readonly rotationY: number; // degrees
    readonly rotationZ: number; // degrees
    readonly fov: number; // degrees
  };
  /** Whether the source file is currently available on disk. */
  readonly isMissing: boolean;
  /** Optional raster mask. */
  readonly mask?: LayerMask;
}

import type { TypeLayer } from "./TypeLayer";

export type Layer = RasterLayer | GroupLayer | TypeLayer | AdjustmentLayer | SmartObjectLayer | LinkedSmartObjectLayer | ShapeLayer | FillLayer | BackgroundLayer | ArtboardLayer | FrameLayer | VideoFrameLayer | ThreeDLayer;

export interface SmartObjectTransform {
  readonly scaleX: number; // default 1
  readonly scaleY: number; // default 1
  readonly rotation: number; // degrees, default 0
  readonly skewX: number; // degrees, default 0
  readonly skewY: number; // degrees, default 0
}

export const DEFAULT_SMART_OBJECT_TRANSFORM: SmartObjectTransform = {
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  skewX: 0,
  skewY: 0,
};

export function createAdjustmentLayer(
  kind: AdjustmentKind,
  params: Record<string, number | string | boolean> = {},
  id?: string,
  name?: string,
): AdjustmentLayer {
  return {
    id: id || `adj-${Date.now()}`,
    name: name || `${kind}`,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    kind: "adjustment",
    adjustment: kind,
    params,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
  };
}

/**
 * Create a Smart Object layer wrapping the given layer's content as an
 * embedded document. The source layer is removed from the parent document
 * and placed inside the Smart Object's embedded document.
 */
export function createSmartObjectLayer(
  sourceLayer: Layer,
  docWidth: number,
  docHeight: number,
  id?: string,
): SmartObjectLayer {
  // Center the Smart Object at the source layer's position
  const soWidth = sourceLayer.kind === "raster" ? sourceLayer.pixels.width : docWidth;
  const soHeight = sourceLayer.kind === "raster" ? sourceLayer.pixels.height : docHeight;

  // Create an embedded document containing the source layer
  const embedded: Document = {
    name: `${sourceLayer.name} (Smart Object)`,
    width: soWidth,
    height: soHeight,
    dpi: 72,
    layers: [sourceLayer],
    activeLayerId: sourceLayer.id,
    selection: null,
  };

  return {
    id: id || `so-${Date.now()}`,
    name: sourceLayer.name,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: sourceLayer.x,
    y: sourceLayer.y,
    kind: "smart-object",
    embedded,
    transform: { ...DEFAULT_SMART_OBJECT_TRANSFORM },
    smartFilters: [],
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
  };
}

export function createShapeLayer(params: {
  path: PathModel;
  fillColor?: { r: number; g: number; b: number; a: number } | null;
  strokeColor?: { r: number; g: number; b: number; a: number } | null;
  strokeWidth?: number;
  id?: string;
  name?: string;
  x?: number;
  y?: number;
}): ShapeLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "shape",
    name: params.name ?? "Shape",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: params.x ?? 0,
    y: params.y ?? 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    path: params.path,
    fillColor: params.fillColor ?? { r: 100, g: 150, b: 255, a: 1 },
    strokeColor: params.strokeColor ?? null,
    strokeWidth: params.strokeWidth ?? 0,
    fillRule: "nonzero",
    lineCap: "round",
    lineJoin: "round",
    miterLimit: 10,
  };
}

export function isShapeLayer(layer: unknown): layer is ShapeLayer {
  return (layer as ShapeLayer)?.kind === "shape";
}

export function createSolidFillLayer(params: {
  color?: { r: number; g: number; b: number; a: number };
  id?: string;
  name?: string;
}): SolidFillLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "solid-fill",
    name: params.name ?? "Color Fill 1",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    color: params.color ?? { r: 255, g: 255, b: 255, a: 1 },
  };
}

export function createGradientFillLayer(params: {
  gradientType?: "linear" | "radial" | "angle" | "reflected" | "diamond";
  angle?: number;
  scale?: number;
  stops?: GradientStop[];
  id?: string;
  name?: string;
}): GradientFillLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "gradient-fill",
    name: params.name ?? "Gradient Fill 1",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    gradientType: params.gradientType ?? "linear",
    angle: params.angle ?? 90,
    scale: params.scale ?? 100,
    reverse: false,
    dither: true,
    stops: params.stops ?? [
      { position: 0, color: { r: 0, g: 0, b: 0, a: 1 } },
      { position: 1, color: { r: 255, g: 255, b: 255, a: 1 } },
    ],
  };
}

export function createPatternFillLayer(params: {
  pattern?: { pixels: Uint8ClampedArray; width: number; height: number } | null;
  patternName?: string;
  scale?: number;
  id?: string;
  name?: string;
}): PatternFillLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "pattern-fill",
    name: params.name ?? "Pattern Fill 1",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    pattern: params.pattern ?? null,
    patternName: params.patternName ?? "None",
    scale: params.scale ?? 100,
    linkWithLayer: true,
  };
}

export function createBackgroundLayer(params: {
  pixels: PixelBuffer;
  id?: string;
  name?: string;
}): BackgroundLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "background",
    name: params.name ?? "Background",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    // lockImage MUST be false: a Photoshop Background layer is fully paintable
    // and fillable (its lock is position-only). The "no transparency" behavior
    // is driven by `kind === "background"` (see BrushTool.eraseToColorFor /
    // isBackgroundLayer, LayerUtils.findRaster), NOT by this flag. Setting it
    // true previously blocked Brush paint-mode, Fill, and Stroke on a fresh
    // white/color document — the eraser was special-cased (i165) but paint/fill
    // were not, so a brand-new document was unpaintable. A user can still lock
    // image pixels explicitly via the Layers panel, and PSD import still
    // honors a real lock flag.
    lockImage: false,
    lockPosition: true,
    lockAll: false,
    clippedToBelow: false,
    pixels: params.pixels,
  };
}

export function createArtboardLayer(params: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  backgroundColor?: { r: number; g: number; b: number; a: number };
  children?: readonly Layer[];
  id?: string;
  name?: string;
}): ArtboardLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "artboard",
    name: params.name ?? "Artboard 1",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    artboardX: params.x ?? 0,
    artboardY: params.y ?? 0,
    artboardWidth: params.width ?? 800,
    artboardHeight: params.height ?? 600,
    backgroundColor: params.backgroundColor ?? { r: 255, g: 255, b: 255, a: 1 },
    children: params.children ?? [],
  };
}

export function createFrameLayer(params: {
  shape?: "rect" | "ellipse" | "rounded-rect";
  cornerRadius?: number;
  width?: number;
  height?: number;
  content?: Layer | null;
  id?: string;
  name?: string;
}): FrameLayer {
  return {
    id: params.id ?? crypto.randomUUID(),
    kind: "frame",
    name: params.name ?? "Frame 1",
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: 0,
    y: 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    shape: params.shape ?? "rect",
    cornerRadius: params.cornerRadius ?? 0,
    content: params.content ?? null,
    frameWidth: params.width ?? 200,
    frameHeight: params.height ?? 200,
  };
}

export function createLinkedSmartObjectLayer(params: {
  filePath: string;
  lastModified?: number;
  composite?: PixelBuffer;
  transform?: SmartObjectTransform;
  smartFilters?: readonly SmartFilterEntry[];
  id?: string;
  name?: string;
  x?: number;
  y?: number;
}): LinkedSmartObjectLayer {
  const base: Omit<LinkedSmartObjectLayer, "lastModified" | "composite"> = {
    id: params.id ?? crypto.randomUUID(),
    kind: "linked-smart-object",
    name: params.name ?? `Linked Smart Object (${params.filePath.split("/").pop() || params.filePath})`,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: params.x ?? 0,
    y: params.y ?? 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    filePath: params.filePath,
    transform: params.transform ?? { ...DEFAULT_SMART_OBJECT_TRANSFORM },
    smartFilters: params.smartFilters ?? [],
    isMissing: false,
  };
  return {
    ...base,
    ...(params.lastModified !== undefined ? { lastModified: params.lastModified } : {}),
    ...(params.composite !== undefined ? { composite: params.composite } : {}),
  } as LinkedSmartObjectLayer;
}

export function isLinkedSmartObjectLayer(layer: unknown): layer is LinkedSmartObjectLayer {
  return (layer as LinkedSmartObjectLayer)?.kind === "linked-smart-object";
}

// ---------------------------------------------------------------------------
// Default camera for 3D layers
// ---------------------------------------------------------------------------
export const DEFAULT_3D_CAMERA: ThreeDLayer["camera"] = {
  x: 0,
  y: 0,
  z: 500,
  rotationX: 0,
  rotationY: 0,
  rotationZ: 0,
  fov: 45,
};

// ---------------------------------------------------------------------------
// Video Frame Layer factory
// ---------------------------------------------------------------------------

/**
 * Create a VideoFrameLayer stub. Full video decoding is deferred to M6 —
 * composite will be undefined until the video pipeline is wired.
 */
export function createVideoFrameLayer(params: {
  filePath: string;
  currentFrame?: number;
  totalFrames?: number;
  fps?: number;
  videoWidth?: number;
  videoHeight?: number;
  composite?: PixelBuffer;
  id?: string;
  name?: string;
  x?: number;
  y?: number;
}): VideoFrameLayer {
  const base: Omit<VideoFrameLayer, "composite"> = {
    id: params.id ?? crypto.randomUUID(),
    kind: "video-frame",
    name: params.name ?? `Video (${params.filePath.split("/").pop() ?? params.filePath})`,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: params.x ?? 0,
    y: params.y ?? 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    filePath: params.filePath,
    currentFrame: params.currentFrame ?? 0,
    totalFrames: params.totalFrames ?? 0,
    fps: params.fps ?? 24,
    videoWidth: params.videoWidth ?? 0,
    videoHeight: params.videoHeight ?? 0,
    isMissing: false,
  };
  return {
    ...base,
    ...(params.composite !== undefined ? { composite: params.composite } : {}),
  } as VideoFrameLayer;
}

export function isVideoFrameLayer(layer: unknown): layer is VideoFrameLayer {
  return (layer as VideoFrameLayer)?.kind === "video-frame";
}

// ---------------------------------------------------------------------------
// 3D Layer factory
// ---------------------------------------------------------------------------

/**
 * Create a ThreeDLayer stub. Full 3D compositing is deferred to M7 —
 * composite will be undefined until the 3D rendering pipeline is wired.
 */
export function createThreeDLayer(params: {
  filePath: string;
  sceneName?: string;
  camera?: Partial<ThreeDLayer["camera"]>;
  composite?: PixelBuffer;
  id?: string;
  name?: string;
  x?: number;
  y?: number;
}): ThreeDLayer {
  const base: Omit<ThreeDLayer, "composite"> = {
    id: params.id ?? crypto.randomUUID(),
    kind: "3d",
    name: params.name ?? `3D (${params.filePath.split("/").pop() ?? params.filePath})`,
    visible: true,
    opacity: 1,
    fillOpacity: 1,
    blendMode: "normal",
    x: params.x ?? 0,
    y: params.y ?? 0,
    lockTransparency: false,
    lockImage: false,
    lockPosition: false,
    lockAll: false,
    clippedToBelow: false,
    filePath: params.filePath,
    sceneName: params.sceneName ?? "Scene",
    camera: { ...DEFAULT_3D_CAMERA, ...(params.camera ?? {}) },
    isMissing: false,
  };
  return {
    ...base,
    ...(params.composite !== undefined ? { composite: params.composite } : {}),
  } as ThreeDLayer;
}

export function isThreeDLayer(layer: unknown): layer is ThreeDLayer {
  return (layer as ThreeDLayer)?.kind === "3d";
}
